import { useState } from 'react';
import { api } from '../api';
import { useDroits } from '../auth';
import { useAction, useApi } from '../hooks';
import { Champ, Chargement, MessageErreur, Modal, Pagination, StatutBool } from '../components/ui';
import { FicheClient } from './ClientDetail';

const ONGLETS = [
  ['tous', 'Tous', 'tous'],
  ['oui', 'Actifs', 'actifs'],
  ['non', 'Inactifs', 'inactifs'],
];

export default function Clients() {
  const { admin } = useDroits();
  const [q, setQ] = useState('');
  const [actif, setActif] = useState('tous');
  const [page, setPage] = useState(1);
  const [creation, setCreation] = useState(false);
  const [ficheId, setFicheId] = useState(null);
  const { donnees, erreur, chargement, recharger } = useApi('/clients', { q, actif, page });

  return (
    <>
      <div className="titre-page">
        <h1>Clients</h1>
        {admin && (
          <button className="btn btn-principal" onClick={() => setCreation(true)}>
            + Nouveau client
          </button>
        )}
      </div>

      <div className="barre-filtres">
        <div className="onglets">
          {ONGLETS.map(([valeur, libelle, compteur]) => (
            <button
              key={valeur}
              className={`onglet ${actif === valeur ? 'actif' : ''}`}
              onClick={() => {
                setActif(valeur);
                setPage(1);
              }}
            >
              {libelle} {donnees && <span className="muted">({donnees.compteurs[compteur]})</span>}
            </button>
          ))}
        </div>
        <input
          type="search"
          placeholder="Rechercher (nom, code, matricule, email)"
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setPage(1);
          }}
        />
      </div>

      <MessageErreur erreur={erreur} />
      {chargement && !donnees ? (
        <Chargement />
      ) : (
        donnees && (
          <div className="carte">
            <table>
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Nom</th>
                  <th>Matricule fiscal</th>
                  <th>Email</th>
                  <th>Postes</th>
                  <th>Statut</th>
                </tr>
              </thead>
              <tbody>
                {donnees.donnees.map((c) => (
                  <tr key={c.id} className="ligne-cliquable" onClick={() => setFicheId(c.id)}>
                    <td>{c.code}</td>
                    <td>
                      <span className="lien">{c.name}</span>
                    </td>
                    <td>{c.taxIdentifier || '—'}</td>
                    <td>{c.email || '—'}</td>
                    <td>{c.nombrePostes}</td>
                    <td>
                      <StatutBool valeur={c.status} />
                    </td>
                  </tr>
                ))}
                {donnees.donnees.length === 0 && (
                  <tr>
                    <td colSpan={6} className="muted">
                      Aucun client.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            <Pagination pagination={donnees.pagination} onPage={setPage} />
          </div>
        )
      )}

      {creation && (
        <CreationClient
          onFermer={() => setCreation(false)}
          onCree={(c) => {
            setCreation(false);
            recharger();
            setFicheId(c.id);
          }}
        />
      )}
      {ficheId && (
        <FicheClient
          id={ficheId}
          onFermer={() => {
            setFicheId(null);
            recharger();
          }}
        />
      )}
    </>
  );
}

// Le client est créé sans poste : ses postes s'ajoutent ensuite depuis sa fiche.
function CreationClient({ onFermer, onCree }) {
  const { executer, enCours, erreur } = useAction();
  const [client, setClient] = useState({ code: '', name: '', taxIdentifier: '', email: '', phone: '', address: '' });
  const [conventionne, setConventionne] = useState(false);

  const champ = (cle) => ({ value: client[cle], onChange: (e) => setClient({ ...client, [cle]: e.target.value }) });

  const soumettre = (e) => {
    e.preventDefault();
    executer(async () => {
      // Les champs vides ne sont pas envoyés
      const corps = Object.fromEntries(Object.entries(client).filter(([, v]) => v.trim() !== ''));
      corps.conventionne = conventionne;
      onCree(await api('/clients', { methode: 'POST', corps }));
    });
  };

  return (
    <Modal titre="Nouveau client" onFermer={onFermer} large>
      <form onSubmit={soumettre}>
        <MessageErreur erreur={erreur} />
        <div className="grille-2">
          <Champ libelle="Code client *" aide="Identifiant unique, non modifiable (ex. CLI-006)">
            <input {...champ('code')} required />
          </Champ>
          <Champ libelle="Nom *">
            <input {...champ('name')} required />
          </Champ>
          <Champ libelle="Matricule fiscal" aide="13 caractères (ex. 1234567ABM000)">
            <input {...champ('taxIdentifier')} minLength={13} maxLength={13} />
          </Champ>
          <Champ libelle="Email">
            <input type="email" {...champ('email')} />
          </Champ>
          <Champ libelle="Téléphone">
            <input {...champ('phone')} />
          </Champ>
          <Champ libelle="Adresse">
            <input {...champ('address')} />
          </Champ>
        </div>
        <label className="case">
          <input type="checkbox" checked={conventionne} onChange={(e) => setConventionne(e.target.checked)} />
          Client conventionné
        </label>

        <div className="actions-modal">
          <button type="button" className="btn btn-secondaire" onClick={onFermer}>
            Annuler
          </button>
          <button className="btn btn-principal" disabled={enCours}>
            {enCours ? 'Création…' : 'Créer le client'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
